import React, { useEffect, useState } from 'react';

import { Button } from '@opendoor/atoms/button';
import { Form } from '@opendoor/atoms/form';
import { Text } from '@opendoor/atoms/text';
import { trackEvent } from '@opendoor/shared-fe/analytics';
import { useForm } from '@tanstack/react-form';
import { useSelector } from '@tanstack/react-store';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

import { Loader } from '../../components/Loader';
import MarketOptions from '../../components/MarketOptions';
import PartnerOptions from '../../components/PartnerOptions';
import { useToast } from '../../contexts/ToastContext';
import { ERROR_PAGE_ROUTE } from '../routes';

type LocationState = {
  email?: string;
  partnershipType?: string;
  role?: string;
  partnerIdentifier?: string;
};

type SelectOption = { value: string; label: string };

const optionSchema = z.object({ value: z.string(), label: z.string() });

const selectedOption = (error: string) =>
  optionSchema.nullable().refine((option): option is SelectOption => option !== null, { error });

/**
 * "Other" is a real choice in both lists, and picking it means the free-text
 * field beside it carries the answer — so it is required exactly then.
 */
const signUpStartSchema = z
  .object({
    partner: selectedOption('Please select your company'),
    otherPartner: z.string(),
    market: selectedOption('Please select your market'),
    otherMarket: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.partner?.value === 'other_builder' && !values.otherPartner.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['otherPartner'],
        message: 'Please tell us who you work for',
      });
    }
    if (values.market?.value === 'other' && !values.otherMarket.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['otherMarket'],
        message: 'Please tell us what city you work in',
      });
    }
  });

const EMPTY_FORM_VALUES: z.input<typeof signUpStartSchema> = {
  partner: null,
  otherPartner: '',
  market: null,
  otherMarket: '',
};

const SignUpStart: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [partnerOptionsLoading, setPartnerOptionsLoading] = useState<boolean>(true);

  const { showToast } = useToast();

  const form = useForm({
    defaultValues: EMPTY_FORM_VALUES,
    validators: { onChange: signUpStartSchema },
    onSubmit: ({ value }) => {
      // Parsing here rather than reading `value` directly is what makes the two
      // selections non-null: the schema's refinements have already run.
      const { partner, otherPartner, market, otherMarket } = signUpStartSchema.parse(value);

      trackEvent.apply(null, [
        'partner-sign-up-flow',
        'account-created',
        partner.value,
        { email: state?.email, role: state?.role },
      ]);

      void navigate('/sign-up/account', {
        state: {
          email: state?.email,
          marketIdentifier: market.value,
          partnerIdentifier: partner.value,
          otherMarketIdentifier: otherMarket,
          otherPartnerIdentifier: otherPartner,
          role: state?.role,
        },
      });
    },
  });

  const partnerIdentifier = useSelector(form.store, (s) => s.values.partner?.value ?? '');
  const marketIdentifier = useSelector(form.store, (s) => s.values.market?.value ?? '');

  useEffect(() => {
    trackEvent.apply(null, ['partner-sign-up-flow', 'start-sign-up', 'page']);

    const userAgent = window.navigator.userAgent;
    const idx = userAgent.indexOf('Edge/');
    if (idx > -1) {
      const version = userAgent.substring(idx + 5, idx + 7);
      if (parseInt(version, 10) < 79) {
        showToast({
          type: 'info',
          message:
            'We do not support this version of Microsoft Edge. ' +
            'Please update your browser at https://www.microsoft.com/en-us/edge, ' +
            'or switch to Chrome or Firefox',
          autoCloseMs: 5000,
        });
      }
    }
  }, [showToast]);

  // Validate location state after all hooks
  if (!state || !state.email || !state.partnershipType || !state.role) {
    return <Navigate to={ERROR_PAGE_ROUTE} replace />;
  }
  const { partnershipType } = state;

  let companyTypeText = 'company';
  switch (partnershipType) {
    case 'HOME_BUILDER':
      companyTypeText = 'home builder';
      break;
    default:
      break;
  }

  return (
    <Form.Root form={form} className="flex max-w-145 flex-col self-center px-1 pt-1">
      <Text
        as="h2"
        className="text-header-sm pb-14 text-left md:text-header-md md:text-center lg:text-header-lg"
      >
        Tell us about yourself
      </Text>
      {partnerOptionsLoading && <Loader size="large" />}
      <PartnerOptions
        partnershipType={partnershipType}
        initialPartnerIdentifier={state.partnerIdentifier}
        title={`Which ${companyTypeText} do you work for?`}
        placeholder="Search or Select"
        showOtherField={partnerIdentifier === 'other_builder'}
        showLoader={setPartnerOptionsLoading}
      />
      <MarketOptions
        partnerIdentifier={partnerIdentifier}
        title="What city do you work in?"
        placeholder="Choose your market"
        isDisabled={!partnerIdentifier}
        showOtherField={marketIdentifier === 'other'}
        partnerOptionsLoading={partnerOptionsLoading}
      />
      <Button
        onClick={() => void form.handleSubmit()}
        variant="primary"
        className="w-full"
        analyticsName="partnership-dashboard-fe-sign-up-continue"
        aria-label="Continue to next step"
      >
        Continue
      </Button>
    </Form.Root>
  );
};

export default SignUpStart;
